import { ArrowRightLeft, Building2, Ear, PhoneOff } from 'lucide-react';

import { PhoebeFlowerIcon } from '@apps/web/components/settings/phoebe_flower_icon';
import { externalForwardingProviderBadgeLabel } from '@apps/web/components/voice_calls/external_forwarding_provider_utils';
import { isVoiceCallListeningOnly } from '@apps/web/components/voice_calls/voice_call_format_utils';
import { formatPhoneNumber } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';
import { type VoiceCallTransferLegRead } from '@libraries/typescript/api_client';

export type VoiceCallTransferLeg = VoiceCallTransferLegRead;

export type VoiceCallPathTraceCall = {
    readonly to_number: string | null;
    readonly inbound_external_forwarding_main_number?: string | null;
    readonly external_forwarding_provider?: string | null;
    readonly external_forwarding_display_name?: string | null;
    readonly transfer_number: string | null;
    readonly transfer_destination_label: string | null;
    readonly transfer_legs?: ReadonlyArray<VoiceCallTransferLeg> | null;
    readonly routing_reason?: string | null;
};

type VoiceCallPathTraceTile = {
    readonly key: string;
    readonly kind: 'office' | 'phoebe' | 'transfer';
    readonly name: string;
    readonly number: string | null;
};

type VoiceCallTransferLegsCall = {
    readonly transfer_number: string | null;
    readonly transfer_destination_label: string | null;
    // Optional: cached API responses from before this field shipped (and
    // partial call objects in tests) may not carry it.
    readonly transfer_legs?: ReadonlyArray<VoiceCallTransferLeg> | null;
};

function getPresentText(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}

export function getVoiceCallTransferLegs(
    call: VoiceCallTransferLegsCall,
): ReadonlyArray<VoiceCallTransferLeg> {
    const transferLegs = call.transfer_legs ?? [];
    if (transferLegs.length > 0) {
        return transferLegs;
    }

    if (call.transfer_destination_label || call.transfer_number) {
        return [
            {
                destination_label: call.transfer_destination_label,
                transfer_number: call.transfer_number,
            },
        ];
    }

    return [];
}

function getDialedLineName(call: VoiceCallPathTraceCall): string {
    const providerLabel = externalForwardingProviderBadgeLabel(
        call.external_forwarding_provider,
    );
    return (
        getPresentText(call.external_forwarding_display_name) ??
        (providerLabel ? `${providerLabel} line` : 'Office line')
    );
}

function buildVoiceCallPathTraceTiles(
    call: VoiceCallPathTraceCall,
    silentListeningLabelSettingsEnabled: boolean,
): Array<VoiceCallPathTraceTile> {
    const tiles: Array<VoiceCallPathTraceTile> = [];

    const dialedNumber = formatPhoneNumber(
        call.inbound_external_forwarding_main_number ?? null,
    );
    if (dialedNumber) {
        tiles.push({
            key: 'office',
            kind: 'office',
            name: getDialedLineName(call),
            number: dialedNumber,
        });
    }

    if (!isVoiceCallListeningOnly(call, silentListeningLabelSettingsEnabled)) {
        tiles.push({
            key: 'phoebe',
            kind: 'phoebe',
            name: 'Phoebe',
            number: formatPhoneNumber(call.to_number),
        });
    }

    for (const transfer of getVoiceCallTransferLegs(call)) {
        const transferName = getPresentText(transfer.destination_label);
        const transferNumber = formatPhoneNumber(transfer.transfer_number);
        if (transferName || transferNumber) {
            tiles.push({
                key: `transfer-${tiles.length}`,
                kind: 'transfer',
                name: transferName ?? 'Unknown destination',
                number: transferNumber,
            });
        }
    }

    return tiles;
}

function VoiceCallPathTileIcon({
    kind,
}: {
    readonly kind: VoiceCallPathTraceTile['kind'];
}) {
    switch (kind) {
        case 'office':
            return <Building2 className="h-4 w-4" />;
        case 'phoebe':
            return <PhoebeFlowerIcon className="h-3.5 w-3.5" />;
        case 'transfer':
            return <ArrowRightLeft className="h-4 w-4" />;
        default:
            return null;
    }
}

export function VoiceCallPathTrace({
    call,
    callerEndedLabel = null,
    silentListeningLabelSettingsEnabled = false,
}: {
    readonly call: VoiceCallPathTraceCall;
    readonly callerEndedLabel?: string | null;
    readonly silentListeningLabelSettingsEnabled?: boolean;
}) {
    const tiles = buildVoiceCallPathTraceTiles(
        call,
        silentListeningLabelSettingsEnabled,
    );
    const isListeningOnly = isVoiceCallListeningOnly(
        call,
        silentListeningLabelSettingsEnabled,
    );
    // A path that is only Phoebe carries no routing story worth showing.
    // Render when the call reached an office line, was transferred, has a
    // listening state to explain, or has a hangup note to surface.
    const hasRoutingStory = tiles.some((tile) => tile.kind !== 'phoebe');
    if (!hasRoutingStory && !callerEndedLabel && !isListeningOnly) {
        return null;
    }

    return (
        <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs">Call path</p>
            <div className="rounded-lg border p-2">
                <div className="relative pl-1">
                    <div
                        aria-hidden="true"
                        className="bg-border absolute top-6 bottom-6 left-[22px] w-px"
                    />
                    {tiles.map((tile) => (
                        <div
                            key={tile.key}
                            className="relative flex items-center gap-3 px-1.5 py-1.5"
                        >
                            <div
                                className={cn(
                                    'z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                                    tile.kind === 'transfer'
                                        ? 'bg-foreground text-background'
                                        : 'bg-muted text-muted-foreground',
                                )}
                            >
                                <VoiceCallPathTileIcon kind={tile.kind} />
                            </div>
                            <div className="min-w-0">
                                <p
                                    className={cn(
                                        'truncate text-sm leading-tight',
                                        tile.kind === 'transfer'
                                            ? 'font-semibold'
                                            : 'font-medium',
                                    )}
                                >
                                    {tile.name}
                                </p>
                                {tile.number ? (
                                    <p className="text-muted-foreground text-xs leading-tight tabular-nums">
                                        {tile.number}
                                    </p>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
                {isListeningOnly ? (
                    <div className="mt-1 flex items-start gap-2 rounded-md bg-sky-50 px-2 py-2 text-sky-950 dark:bg-sky-950/30 dark:text-sky-100">
                        <Ear className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600 dark:text-sky-300" />
                        <div className="min-w-0">
                            <p className="text-xs font-medium">
                                Phoebe listened silently
                            </p>
                            <p className="text-xs text-sky-700 dark:text-sky-300">
                                Did not answer or speak
                            </p>
                        </div>
                    </div>
                ) : null}
                {callerEndedLabel ? (
                    <div className="text-muted-foreground mt-1 flex items-center gap-1.5 px-1.5 text-xs">
                        <PhoneOff className="h-3.5 w-3.5" />
                        <span>{callerEndedLabel}</span>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
